import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — ForexMind AI" },
      { name: "description", content: "How ForexMind AI collects, uses, and protects your personal data." },
      { name: "robots", content: "index,follow" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "Privacy Policy — ForexMind AI" },
      { property: "og:description", content: "How ForexMind AI collects, uses, and protects your personal data." },
      { property: "og:url", content: "https://forexmind.ai/privacy" },
      { property: "og:image", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Privacy Policy — ForexMind AI" },
      { name: "twitter:description", content: "How ForexMind AI collects, uses, and protects your personal data." },
      { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
    ],
    links: [{ rel: "canonical", href: "https://forexmind.ai/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 text-foreground">
      <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to home</Link>
      <h1 className="mt-6 text-3xl font-bold">Privacy Policy</h1>
      <p className="mt-2 text-xs text-muted-foreground">Last updated: June 2026</p>
      <div className="mt-8 space-y-6 text-sm leading-relaxed text-muted-foreground">
        <p className="rounded-lg border border-border bg-card p-4">
          This Privacy Policy describes how ForexMind AI ("we", "us") collects, uses, and
          protects your personal information when you use our service. If you have any
          questions about this policy, please contact us at <a className="underline" href="mailto:legal@forexmind.ai">legal@forexmind.ai</a>.
        </p>
        <h2 className="text-lg font-semibold text-foreground">Data we collect</h2>
        <ul className="list-disc space-y-1 pl-6">
          <li>Account: email address, display name, hashed password (via Supabase Auth).</li>
          <li>Usage: pages viewed, signals saved, trading-pair preferences.</li>
          <li>Payment: handled by Stripe — we never see or store full card numbers.</li>
          <li>Technical: IP address, browser type, error telemetry (PII stripped).</li>
        </ul>
        <h2 className="text-lg font-semibold text-foreground">How we use it</h2>
        <p>To operate the service, deliver signals, process subscription payments, and improve the platform. We do not sell your personal data.</p>
        <h2 className="text-lg font-semibold text-foreground">Subprocessors</h2>
        <ul className="list-disc space-y-1 pl-6">
          <li>Supabase — auth and database hosting</li>
          <li>Stripe — payments</li>
          <li>Anthropic, xAI — AI model inference (only sanitised market context is sent)</li>
          <li>Sentry — error monitoring (PII stripped client-side)</li>
        </ul>
        <h2 className="text-lg font-semibold text-foreground">Your rights (GDPR / CCPA)</h2>
        <p>You can export, correct, or delete your data at any time from <Link to="/settings" className="underline">Settings</Link>. Account deletion is irreversible and cancels active subscriptions.</p>
        <h2 className="text-lg font-semibold text-foreground">Contact</h2>
        <p>For privacy questions, email <a className="underline" href="mailto:legal@forexmind.ai">legal@forexmind.ai</a>.</p>
      </div>
    </main>
  );
}
